import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, App, Button, Card, Checkbox, Col, Empty, Form, Input, InputNumber, Modal, Progress, Row, Select, Space, Tag, Typography, Upload, theme } from "antd";
import {
  LOCATION_BROWSE_LIMIT,
  locationBatchTooMany,
  locationDeletePlanText,
  locationDeleteTooMany,
  locationRangeText,
  previewLocationBatch,
  type LocationGenerateInput,
  type LocationImportRow,
  type LocationLabelRange,
} from "@shoe-trace/shared";
import {
  batchLocations,
  correctLocation,
  deleteLocations,
  downloadFile,
  generateLocations,
  listLocations,
  previewImport,
  previewLocationGenerate,
  commitImport,
  resolvePutaway,
  updateLocation,
  type ImportPreview,
  type LocationCard,
  type LocationList,
  type PutawayTarget,
} from "../api/client";
import { LoadFail, PageSpin, PasswordModal, ScanBox } from "../components/widgets";
import { errorText, parseLocationScan } from "../scan/actions";
import { canManage, useSession } from "../session";
import { LOCATION_IMPORT_DROP_HINT, LOCATION_IMPORT_TEMPLATE } from "./location-import-template";

type PasswordKind = "correct" | "lock-batch" | "normal-batch" | "delete" | "lock-one" | "normal-one";
type PlaceKind = "vacant" | "stocked" | "full" | "locked";
type StatusFilter = "all" | PlaceKind;
type ScopeKind = "picked" | "warehouse" | "status" | "filtered";

interface BatchScope {
  range: LocationLabelRange;
  rows: LocationCard[] | null;
  count: number;
  empty: number;
  kept: number;
  phrase: string;
}

interface PrintPlan {
  count: number;
  perPage: number;
  sheets: number;
  range: LocationLabelRange;
  codes: string[] | null;
  phrase: string;
}

interface CapacityTarget {
  scope: BatchScope;
  card: LocationCard | null;
}

interface GenerateFields {
  zone?: string;
  rackFrom?: number | null;
  rackTo?: number | null;
  shelfFrom?: number | null;
  shelfTo?: number | null;
  binFrom?: number | null;
  binTo?: number | null;
  capacity?: number | null;
}

function readError(error: unknown): string {
  return error instanceof Error ? error.message : "这一步没有完成。请稍后再试一次。";
}

function wholeNumber(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

function generateInput(values: GenerateFields | undefined): LocationGenerateInput | null {
  if (!values) return null;
  const zone = values.zone?.trim().toUpperCase() ?? "";
  const rackFrom = wholeNumber(values.rackFrom);
  const rackTo = wholeNumber(values.rackTo);
  const shelfFrom = wholeNumber(values.shelfFrom);
  const shelfTo = wholeNumber(values.shelfTo);
  const binFrom = wholeNumber(values.binFrom);
  const binTo = wholeNumber(values.binTo);
  if (!/^[A-Z]$/.test(zone) || rackFrom === null || rackTo === null) return null;
  return {
    zone,
    rackFrom,
    rackTo,
    shelfFrom,
    shelfTo,
    binFrom,
    binTo,
    capacity: wholeNumber(values.capacity) ?? 50,
  };
}

function isLocationImportRow(row: object): row is LocationImportRow {
  return "code" in row && "capacity" in row && "status" in row;
}

type PlaceCounts = { vacant: number; stocked: number; full: number; locked: number };

function placeKind(row: LocationCard): PlaceKind {
  if (row.status === "LOCKED") return "locked";
  if (row.status === "FULL") return "full";
  if (row.occupied > 0) return "stocked";
  return "vacant";
}

function countPlaces(rows: readonly LocationCard[]): PlaceCounts {
  const counts: PlaceCounts = { vacant: 0, stocked: 0, full: 0, locked: 0 };
  for (const row of rows) counts[placeKind(row)] += 1;
  return counts;
}

function locationScopePhrase(kind: ScopeKind, count: number): string {
  if (kind === "picked") return `挑选的 ${count} 个库位`;
  if (kind === "warehouse") return `仓库里全部 ${count} 个库位`;
  if (kind === "status") return `当前状态下的 ${count} 个库位`;
  return `当前筛出的 ${count} 个库位`;
}

function scopeKind(input: {
  picked: boolean;
  zone?: string;
  rack?: string;
  shelf?: string;
  status: StatusFilter;
}): ScopeKind {
  if (input.picked) return "picked";
  const placeEmpty = !input.zone && !input.rack && !input.shelf;
  if (placeEmpty && input.status === "all") return "warehouse";
  if (placeEmpty) return "status";
  return "filtered";
}

function emptyAmong(rows: readonly LocationCard[]): number {
  return rows.filter((row) => row.removable).length;
}

function occupancyPercent(occupied: number, capacity: number): number {
  if (capacity <= 0 || occupied <= 0) return 0;
  if (occupied >= capacity) return 100;
  return Math.min(100, Math.round((occupied / capacity) * 100));
}

function statusMark(row: LocationCard): { color: string; label: string } {
  const kind = placeKind(row);
  if (kind === "locked") return { color: "error", label: "已锁定" };
  if (kind === "full") return { color: "warning", label: "已满" };
  if (kind === "stocked") return { color: "processing", label: "有货" };
  return { color: "default", label: "空置" };
}

export function Locations(): JSX.Element {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { user } = useSession();
  const manager = canManage(user);
  const [list, setList] = useState<LocationList | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [zone, setZone] = useState<string | undefined>(undefined);
  const [rack, setRack] = useState<string | undefined>(undefined);
  const [shelf, setShelf] = useState<string | undefined>(undefined);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [scope, setScope] = useState<BatchScope | null>(null);
  const [cardTarget, setCardTarget] = useState<LocationCard | null>(null);
  const [passwordOpen, setPasswordOpen] = useState<PasswordKind | null>(null);
  const [capacityTarget, setCapacityTarget] = useState<CapacityTarget | null>(null);
  const [capacityValue, setCapacityValue] = useState<number | null>(50);
  const [capacityPassword, setCapacityPassword] = useState("");
  const [capacitySaving, setCapacitySaving] = useState(false);
  const [printPlan, setPrintPlan] = useState<PrintPlan | null>(null);
  const [printNotice, setPrintNotice] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [correctShoe, setCorrectShoe] = useState<PutawayTarget | null>(null);
  const [correctCode, setCorrectCode] = useState("");
  const [correctError, setCorrectError] = useState<string | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());

  const load = useCallback(() => {
    setLoading(true);
    void listLocations({ zone, rack, shelf })
      .then((page) => {
        setList(page);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [rack, shelf, zone]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!list) return;
    if (list.total > 0 && list.rows.length !== list.total) setStatusFilter("all");
  }, [list]);

  useEffect(() => {
    if (!list) return;
    setPicked((current) => {
      if (current.size === 0) return current;
      const alive = new Set(list.rows.map((row) => row.code));
      let changed = false;
      const next = new Set<string>();
      for (const code of current) {
        if (alive.has(code)) next.add(code);
        else changed = true;
      }
      return changed ? next : current;
    });
  }, [list]);

  if (loading && !list) return <PageSpin tip="正在读取库位…" />;
  if (failed || !list) return <LoadFail title="库位没有更新。" onRetry={load} />;

  const fullyLoaded = list.rows.length === list.total;
  const effectiveStatus: StatusFilter = fullyLoaded ? statusFilter : "all";
  const statusActive = effectiveStatus !== "all";
  const visible = statusActive ? list.rows.filter((row) => placeKind(row) === effectiveStatus) : list.rows;
  const pickedRows = visible.filter((row) => picked.has(row.code));
  const hasPick = pickedRows.length > 0;
  const actionCount = fullyLoaded ? visible.length : list.total;
  const placeCounts = fullyLoaded ? countPlaces(list.rows) : null;
  const activeRange: LocationLabelRange = { zone: zone ?? null, rack: rack ?? null, shelf: shelf ?? null };
  const scopedEmpty = fullyLoaded ? emptyAmong(visible) : list.empty;
  const scopedKept = fullyLoaded ? visible.length - scopedEmpty : list.kept;
  const actingCount = hasPick ? pickedRows.length : actionCount;
  const emptyCount = hasPick ? emptyAmong(pickedRows) : scopedEmpty;
  const keptCount = hasPick ? pickedRows.length - emptyCount : scopedKept;
  const phrase = locationScopePhrase(scopeKind({
    picked: hasPick,
    zone,
    rack,
    shelf,
    status: effectiveStatus,
  }), actingCount);
  const printPreview = previewLocationBatch(actingCount, activeRange);
  const batchLimit = locationBatchTooMany(actingCount);
  const deleteLimit = locationDeleteTooMany(emptyCount);
  const showCreate = user?.role === "ADMIN" || manager;
  const allVisiblePicked = visible.length > 0 && visible.every((row) => picked.has(row.code));
  const canPickAll = fullyLoaded && visible.length > 0 && !allVisiblePicked;

  function forgetPick(): void {
    setPicked(new Set());
  }

  function togglePick(code: string, on: boolean): void {
    setPicked((current) => {
      const next = new Set(current);
      if (on) next.add(code);
      else next.delete(code);
      return next;
    });
  }

  function pickVisible(): void {
    setPicked((current) => {
      const next = new Set(current);
      for (const row of visible) next.add(row.code);
      return next;
    });
  }

  function rememberScope(): BatchScope {
    const count = hasPick ? pickedRows.length : actionCount;
    return {
      range: activeRange,
      rows: hasPick ? [...pickedRows] : statusActive ? [...visible] : null,
      count,
      empty: emptyCount,
      kept: keptCount,
      phrase: locationScopePhrase(scopeKind({
        picked: hasPick,
        zone,
        rack,
        shelf,
        status: effectiveStatus,
      }), count),
    };
  }

  function askPrint(): void {
    if (printPreview.status !== "ready") {
      setPrintNotice(printPreview.message);
      return;
    }
    setPrintPlan({
      count: actingCount,
      perPage: printPreview.perPage,
      sheets: printPreview.sheets,
      range: activeRange,
      codes: hasPick || statusActive ? (hasPick ? pickedRows : visible).map((row) => row.code) : null,
      phrase,
    });
  }

  function openBatchPassword(kind: "lock-batch" | "normal-batch" | "delete"): void {
    setCardTarget(null);
    setScope(rememberScope());
    setPasswordOpen(kind);
  }

  function openCardPassword(row: LocationCard, kind: "lock-one" | "normal-one"): void {
    setCardTarget(row);
    setPasswordOpen(kind);
  }

  function openCapacity(card: LocationCard | null): void {
    setCapacityTarget({ scope: rememberScope(), card });
    setCapacityValue(card ? card.capacity : 50);
    setCapacityPassword("");
  }

  async function saveCapacity(capacity: number, password: string): Promise<void> {
    const target = capacityTarget;
    if (!target) return;
    if (target.card) {
      await updateLocation(target.card.code, { capacity, status: target.card.status, password });
      message.success("这个库位的容量已保存。");
      return;
    }
    if (target.scope.rows) {
      for (const row of target.scope.rows) {
        await updateLocation(row.code, { capacity, status: row.status, password });
      }
    } else {
      await batchLocations({ ...target.scope.range, capacity, password });
    }
    message.success("这批库位的容量已保存。");
  }

  function printOne(row: LocationCard): void {
    void downloadFile(`/api/locations/${row.code}/label.pdf`, `${row.code}.pdf`)
      .then(() => {
        message.success("库位码文件已下载。贴到库位上后再上架。");
      })
      .catch((error: unknown) => message.error(readError(error)));
  }

  const cappedMessage = !activeRange.zone && list.total > LOCATION_BROWSE_LIMIT
    ? "请先定一个面"
    : countLine(list.total, printPreview.message, printPreview.status);

  return (
    <>
      {showCreate ? (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          {user?.role === "ADMIN" ? <Typography.Text>管理员账号，扫码请换作业员工号</Typography.Text> : null}
          {manager ? (
            <Space>
              <Button onClick={() => setImportOpen(true)}>导入库位清单</Button>
              <Button type="primary" onClick={() => setGenerateOpen(true)}>批量生成库位</Button>
            </Space>
          ) : null}
        </div>
      ) : null}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          background: token.colorBgLayout,
          paddingBottom: 16,
        }}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Space wrap>
            <Space>
              <Typography.Text>面</Typography.Text>
              <Select<string>
                allowClear
                placeholder="全部面"
                value={zone}
                style={{ width: 140 }}
                options={list.zones.map((item) => ({ value: item, label: item }))}
                onChange={(value) => {
                  setZone(value);
                  setRack(undefined);
                  setShelf(undefined);
                  forgetPick();
                }}
              />
            </Space>
            <Space>
              <Typography.Text>架</Typography.Text>
              <Select<string>
                allowClear
                placeholder="全部架"
                disabled={!zone}
                value={rack}
                style={{ width: 140 }}
                options={list.racks.map((item) => ({ value: item, label: String(Number(item)) }))}
                onChange={(value) => {
                  setRack(value);
                  setShelf(undefined);
                  forgetPick();
                }}
              />
            </Space>
            <Space>
              <Typography.Text>层</Typography.Text>
              <Select<string>
                allowClear
                placeholder="全部层"
                disabled={!zone || !rack}
                value={shelf}
                style={{ width: 140 }}
                options={list.shelves.map((item) => ({ value: item, label: String(Number(item)) }))}
                onChange={(value) => {
                  setShelf(value);
                  forgetPick();
                }}
              />
            </Space>
            <Space>
              <Typography.Text>状态</Typography.Text>
              <Select<StatusFilter>
                disabled={!fullyLoaded && list.total > 0}
                value={effectiveStatus}
                style={{ width: 140 }}
                options={[
                  { value: "all", label: "全部" },
                  { value: "vacant", label: "空置" },
                  { value: "stocked", label: "有货" },
                  { value: "full", label: "已满" },
                  { value: "locked", label: "已锁定" },
                ]}
                onChange={(value) => {
                  setStatusFilter(value);
                  forgetPick();
                }}
              />
            </Space>
            {placeCounts ? (
              <Typography.Text>
                {`空置 ${placeCounts.vacant}，有货 ${placeCounts.stocked}，已满 ${placeCounts.full}，已锁定 ${placeCounts.locked}`}
              </Typography.Text>
            ) : null}
          </Space>
          {manager ? (
            <Space wrap>
              <Typography.Text>{`对${phrase}：`}</Typography.Text>
              <Button disabled={!canPickAll} onClick={pickVisible}>勾上当前筛出的全部</Button>
              <Button disabled={actingCount < 1} loading={downloading} onClick={askPrint}>打印库位码</Button>
              <Button disabled={actingCount < 1 || Boolean(batchLimit)} onClick={() => openCapacity(null)}>修改容量</Button>
              <Button disabled={actingCount < 1 || Boolean(batchLimit)} onClick={() => openBatchPassword("lock-batch")}>锁定库位</Button>
              <Button disabled={actingCount < 1 || Boolean(batchLimit)} onClick={() => openBatchPassword("normal-batch")}>解锁库位</Button>
              <Button
                danger
                disabled={actingCount < 1 || emptyCount < 1 || Boolean(deleteLimit)}
                onClick={() => openBatchPassword("delete")}
              >
                删除空库位
              </Button>
              {hasPick ? <Button onClick={forgetPick}>清掉勾选</Button> : null}
            </Space>
          ) : null}
        </Space>
      </div>
      {batchLimit ? <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={batchLimit} /> : null}
      {deleteLimit ? <Alert style={{ marginBottom: 12 }} type="warning" showIcon message={deleteLimit} /> : null}
      {list.total === 0 ? (
        list.zones.length === 0
          ? <Empty description="还没有库位。请主管生成或导入库位清单。" />
          : <Alert type="info" showIcon message="这个范围还没有库位。换一个面或架。" />
      ) : !fullyLoaded ? (
        <Alert type="warning" showIcon message={cappedMessage} />
      ) : visible.length > 0 ? (
        <Row align="stretch" gutter={[12, 12]}>
          {visible.map((row) => {
            const mark = statusMark(row);
            const hasShoes = row.occupied > 0;
            const chosen = picked.has(row.code);
            const background = chosen ? token.colorPrimaryBg : "#fff";
            return (
              <Col key={row.code} xs={24} sm={12} md={8} lg={6} style={{ display: "flex" }}>
                <Card
                  variant="outlined"
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    background,
                    borderColor: chosen ? token.colorPrimary : undefined,
                    boxShadow: chosen ? `0 0 0 1px ${token.colorPrimary}` : undefined,
                  }}
                  styles={{
                    header: { background },
                    body: {
                      background,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    },
                  }}
                  title={manager ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Checkbox
                        className="location-pick"
                        checked={chosen}
                        onChange={(event) => togglePick(row.code, event.target.checked)}
                      >
                        <span className="location-pick-name">{`把库位 ${row.code} 加入这批操作`}</span>
                      </Checkbox>
                      <span>{row.code}</span>
                    </span>
                  ) : row.code}
                  extra={<Tag color={mark.color}>{mark.label}</Tag>}
                >
                  <div style={{ flex: 1, minHeight: 22 }}>
                    {row.batches.map((batch) => (
                      <Tag key={batch.batchNo}>{batch.batchNo} × {batch.count}</Tag>
                    ))}
                  </div>
                  <Progress
                    style={{ margin: "8px 0" }}
                    percent={occupancyPercent(row.occupied, row.capacity)}
                    showInfo={false}
                    size={["100%", 8]}
                  />
                  <div>{`占用 ${row.occupied} / 容量 ${row.capacity} 双`}</div>
                  <Space wrap size={[8, 8]} style={{ marginTop: 12 }}>
                    {manager ? <Button size="small" onClick={() => printOne(row)}>打印这个库位码</Button> : null}
                    {manager ? <Button size="small" onClick={() => openCapacity(row)}>改这个库位的容量</Button> : null}
                    {manager && row.status === "LOCKED" ? (
                      <Button size="small" onClick={() => openCardPassword(row, "normal-one")}>解锁这个库位</Button>
                    ) : null}
                    {manager && row.status !== "LOCKED" ? (
                      <Button size="small" onClick={() => openCardPassword(row, "lock-one")}>锁定这个库位</Button>
                    ) : null}
                    {hasShoes ? (
                      <Button
                        size="small"
                        onClick={() => {
                          setCorrectShoe(null);
                          setCorrectCode("");
                          setCorrectError(null);
                          setCorrectOpen(true);
                        }}
                      >
                        把这个库位上的鞋移走
                      </Button>
                    ) : null}
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Alert type="info" showIcon message="这个状态下没有库位。换一个状态，或清掉状态筛选。" />
      )}
      <Modal
        destroyOnClose
        open={printPlan !== null}
        title={printPlan ? `下载${printPlan.phrase}码。每页 ${printPlan.perPage} 个，共 ${printPlan.sheets} 页。` : ""}
        okText="下载库位码"
        cancelText="先不下载"
        confirmLoading={downloading}
        onCancel={() => setPrintPlan(null)}
        onOk={() => {
          if (!printPlan) return Promise.resolve();
          const params = new URLSearchParams();
          if (printPlan.range.zone) params.set("zone", printPlan.range.zone);
          if (printPlan.range.rack) params.set("rack", printPlan.range.rack);
          if (printPlan.range.shelf) params.set("shelf", printPlan.range.shelf);
          if (printPlan.codes && printPlan.codes.length > 0) params.set("codes", printPlan.codes.join(","));
          const suffix = params.toString();
          const filename = `库位码-${locationRangeText(printPlan.range)}.pdf`;
          setDownloading(true);
          return downloadFile(`/api/locations/labels.pdf${suffix ? `?${suffix}` : ""}`, filename)
            .then(() => {
              message.success("库位码已下载。按页码顺序打印，再按编码顺序贴到货位上。");
              setPrintPlan(null);
            })
            .catch((error: unknown) => {
              message.error(readError(error));
              return Promise.reject(error instanceof Error ? error : new Error(readError(error)));
            })
            .finally(() => setDownloading(false));
        }}
      />
      <Modal
        open={printNotice !== null}
        title={printNotice ?? ""}
        okText="先不下载"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => setPrintNotice(null)}
        onCancel={() => setPrintNotice(null)}
      />
      <Modal
        destroyOnClose
        open={capacityTarget !== null}
        title={capacityTitle(capacityTarget)}
        okText={capacityTarget?.card ? "保存这个库位的容量" : "保存这些库位的容量"}
        cancelText="先不改容量"
        confirmLoading={capacitySaving}
        onCancel={() => setCapacityTarget(null)}
        afterClose={() => {
          setCapacityPassword("");
          setCapacityValue(50);
        }}
        onOk={() => {
          const capacity = wholeNumber(capacityValue);
          if (capacity === null || capacity < 1 || capacity > 100000) {
            message.warning("请填写能放多少双。");
            return Promise.reject(new Error("capacity"));
          }
          if (!capacityPassword.trim()) {
            message.warning("密码还空着。请输入你的登录密码后再继续。");
            return Promise.reject(new Error("empty"));
          }
          setCapacitySaving(true);
          return saveCapacity(capacity, capacityPassword)
            .then(() => {
              forgetPick();
              setCapacityTarget(null);
              load();
            })
            .catch((error: unknown) => {
              message.error(readError(error));
              load();
              return Promise.reject(error instanceof Error ? error : new Error(readError(error)));
            })
            .finally(() => setCapacitySaving(false));
        }}
      >
        <Typography.Paragraph>
          {capacityLead(capacityTarget)}
        </Typography.Paragraph>
        <InputNumber
          min={1}
          max={100000}
          value={capacityValue}
          style={{ width: "100%", marginBottom: 12 }}
          onChange={(value) => setCapacityValue(typeof value === "number" ? value : null)}
        />
        <Typography.Paragraph style={{ marginBottom: 8 }}>请再输入一次你的密码</Typography.Paragraph>
        <Input.Password
          value={capacityPassword}
          autoFocus
          onChange={(event) => setCapacityPassword(event.target.value)}
        />
      </Modal>
      <Modal
        destroyOnClose
        open={correctOpen}
        title="更正这批所在库位"
        okText="继续确认本人"
        cancelText="先不操作"
        okButtonProps={{ disabled: !correctShoe || !parseLocationScan(correctCode, "这不是库位码。请扫库位上的那张。").ok }}
        onCancel={() => setCorrectOpen(false)}
        onOk={() => {
          setCorrectOpen(false);
          setPasswordOpen("correct");
        }}
      >
        <Alert type="info" showIcon message="先扫一只鞋认出这一行，再扫新库位。目标库位不能已经放着另一张到货单。" />
        <Typography.Paragraph style={{ marginTop: 12 }}>1. 扫描一只鞋</Typography.Paragraph>
        <ScanBox
          placeholder="扫描这双的外标或吊牌"
          onSubmit={async (code) => {
            try {
              const found = await resolvePutaway(code);
              setCorrectError(null);
              setCorrectShoe(found);
            } catch (error: unknown) {
              setCorrectShoe(null);
              setCorrectError(errorText(error, "这不是鞋盒标或吊牌。请先扫鞋，再扫库位。"));
            }
          }}
        />
        {correctShoe ? <Typography.Paragraph>批次 {correctShoe.batchNo} · {correctShoe.humanCode}</Typography.Paragraph> : null}
        <Typography.Paragraph>2. 扫描新库位码</Typography.Paragraph>
        <ScanBox
          placeholder="扫描新的库位码"
          onSubmit={async (code) => {
            const parsed = parseLocationScan(code, "这不是库位码。请扫库位上的那张。");
            if (!parsed.ok) {
              setCorrectError(parsed.message);
              setCorrectCode("");
              return;
            }
            setCorrectError(null);
            setCorrectCode(parsed.locationCode);
          }}
        />
        {correctCode ? <Typography.Text>改到 {correctCode}</Typography.Text> : null}
        {correctError ? <Alert style={{ marginTop: 12 }} type="error" showIcon message={correctError} /> : null}
      </Modal>
      <GenerateLocationsModal
        open={generateOpen}
        onCancel={() => setGenerateOpen(false)}
        onDone={() => {
          setGenerateOpen(false);
          load();
        }}
      />
      <ImportLocationsModal
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        onDone={() => {
          setImportOpen(false);
          load();
        }}
      />
      <PasswordModal
        open={passwordOpen !== null}
        okText={passwordOkText(passwordOpen, scope)}
        note={passwordNote(passwordOpen, scope)}
        onCancel={() => {
          setPasswordOpen(null);
          setCardTarget(null);
        }}
        onConfirm={async (password) => {
          try {
            if (passwordOpen === "correct" && correctShoe) {
              const result = await correctLocation({
                batchId: correctShoe.batchId,
                newLocationCode: correctCode,
                password,
              });
              message.success(`这批已改到 ${result.locationCode}。`);
            } else if (passwordOpen === "lock-one" && cardTarget) {
              await updateLocation(cardTarget.code, { capacity: cardTarget.capacity, status: "LOCKED", password });
              message.success("这个库位已锁定。");
              forgetPick();
            } else if (passwordOpen === "normal-one" && cardTarget) {
              await updateLocation(cardTarget.code, { capacity: cardTarget.capacity, status: "NORMAL", password });
              message.success("这个库位已解锁。");
              forgetPick();
            } else if (passwordOpen === "lock-batch" && scope) {
              if (scope.rows) {
                for (const row of scope.rows) {
                  await updateLocation(row.code, { capacity: row.capacity, status: "LOCKED", password });
                }
              } else {
                await batchLocations({ ...scope.range, status: "LOCKED", password });
              }
              message.success("这批库位已锁定。");
              forgetPick();
            } else if (passwordOpen === "normal-batch" && scope) {
              if (scope.rows) {
                for (const row of scope.rows) {
                  await updateLocation(row.code, { capacity: row.capacity, status: "NORMAL", password });
                }
              } else {
                await batchLocations({ ...scope.range, status: "NORMAL", password });
              }
              message.success("这批库位已解锁。");
              forgetPick();
            } else if (passwordOpen === "delete" && scope) {
              if (scope.rows) {
                let deleted = 0;
                for (const row of scope.rows) {
                  if (!row.removable) continue;
                  await deleteLocations({ code: row.code, password });
                  deleted += 1;
                }
                message.success(locationDeletePlanText(deleted, scope.kept));
              } else {
                const result = await deleteLocations({ ...scope.range, password });
                message.success(locationDeletePlanText(result.deleted, result.kept));
              }
              forgetPick();
            }
          } finally {
            load();
          }
        }}
      />
    </>
  );
}

function capacityTitle(target: CapacityTarget | null): string {
  if (!target) return "";
  if (target.card) return "改这个库位的容量";
  return `修改${target.scope.phrase}的容量`;
}

function capacityLead(target: CapacityTarget | null): string {
  if (!target) return "";
  if (target.card) return `把库位 ${target.card.code} 改成这个容量`;
  return `把${target.scope.phrase}改成这个容量`;
}

function passwordOkText(kind: PasswordKind | null, scope: BatchScope | null): string {
  if (kind === "lock-one") return "锁定这个库位";
  if (kind === "normal-one") return "解锁这个库位";
  if (kind === "lock-batch" && scope) return `锁定${scope.phrase}`;
  if (kind === "normal-batch" && scope) return `解锁${scope.phrase}`;
  if (kind === "delete") return "删除这些空库位";
  return "确认本人并更正库位";
}

function passwordNote(kind: PasswordKind | null, scope: BatchScope | null): string | undefined {
  if (!scope) return undefined;
  if (kind === "delete") return locationDeletePlanText(scope.empty, scope.kept);
  if (kind === "lock-batch") return `锁定${scope.phrase}`;
  if (kind === "normal-batch") return `解锁${scope.phrase}`;
  return undefined;
}

function countLine(total: number, message: string, status: string): string {
  if (status === "too-many") return `这一批有 ${total} 个库位。${message}`;
  if (status === "ready") return message;
  return message;
}

function GenerateLocationsModal({
  open,
  onCancel,
  onDone,
}: {
  open: boolean;
  onCancel: () => void;
  onDone: () => void;
}): JSX.Element {
  const { message } = App.useApp();
  const [form] = Form.useForm<GenerateFields>();
  const [fields, setFields] = useState<GenerateFields>({ capacity: 50 });
  const [plan, setPlan] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const input = generateInput(fields);
  const requestRef = useRef<LocationGenerateInput | null>(null);
  requestRef.current = input;
  const signature = input
    ? `${input.zone}:${input.rackFrom}:${input.rackTo}:${input.shelfFrom}:${input.shelfTo}:${input.binFrom}:${input.binTo}:${input.capacity}`
    : "";

  useEffect(() => {
    const current = requestRef.current;
    if (!open || !current) {
      setPlan(null);
      setBlocked(null);
      return;
    }
    const handle = window.setTimeout(() => {
      void previewLocationGenerate(current)
        .then((result) => {
          setPlan(result.message);
          setBlocked(null);
        })
        .catch((error: unknown) => {
          setPlan(null);
          setBlocked(readError(error));
        });
    }, 200);
    return () => window.clearTimeout(handle);
  }, [open, signature]);

  return (
    <Modal
      destroyOnClose
      open={open}
      title="批量生成库位"
      okText="生成这批库位"
      cancelText="先不生成"
      okButtonProps={{ disabled: !plan || Boolean(blocked), loading: saving }}
      onCancel={onCancel}
      afterClose={() => {
        setFields({ capacity: 50 });
        setPlan(null);
        setBlocked(null);
      }}
      onOk={() => {
        const next = generateInput(form.getFieldsValue());
        if (!next) return;
        setSaving(true);
        void generateLocations(next)
          .then((result) => {
            message.success(result.message);
            onDone();
          })
          .catch((error: unknown) => message.error(readError(error)))
          .finally(() => setSaving(false));
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ capacity: 50 }}
        onValuesChange={(_changed, all) => setFields(all)}
      >
        <Typography.Paragraph type="secondary">
          整面一个库位时，层和位留空。只要某一层时，只填层。选了具体层之后，没分层的库位不会出现在列表里。
        </Typography.Paragraph>
        <Form.Item label="面" name="zone" rules={[{ required: true, message: "面要是一位大写字母，例如 A。" }]}>
          <Input maxLength={1} placeholder="A" />
        </Form.Item>
        <Space wrap>
          <Form.Item label="架起号" name="rackFrom" rules={[{ required: true, message: "请填写架的起号。" }]}>
            <InputNumber min={1} max={99} />
          </Form.Item>
          <Form.Item label="架止号" name="rackTo" rules={[{ required: true, message: "请填写架的止号。" }]}>
            <InputNumber min={1} max={99} />
          </Form.Item>
        </Space>
        <Space wrap>
          <Form.Item label="层起号" name="shelfFrom">
            <InputNumber min={1} max={99} />
          </Form.Item>
          <Form.Item label="层止号" name="shelfTo">
            <InputNumber min={1} max={99} />
          </Form.Item>
        </Space>
        <Space wrap>
          <Form.Item label="位起号" name="binFrom">
            <InputNumber min={1} max={99} />
          </Form.Item>
          <Form.Item label="位止号" name="binTo">
            <InputNumber min={1} max={99} />
          </Form.Item>
        </Space>
        <Form.Item label="容量" name="capacity" rules={[{ required: true, message: "请填写能放多少双。" }]}>
          <InputNumber min={1} max={100000} style={{ width: "100%" }} />
        </Form.Item>
      </Form>
      {plan ? <Alert type="info" showIcon message={plan} /> : null}
      {blocked ? <Alert style={{ marginTop: 12 }} type="warning" showIcon message={blocked} /> : null}
    </Modal>
  );
}

function ImportLocationsModal({
  open,
  onCancel,
  onDone,
}: {
  open: boolean;
  onCancel: () => void;
  onDone: () => void;
}): JSX.Element {
  const { message } = App.useApp();
  const [preview, setPreview] = useState<ImportPreview<LocationImportRow> | null>(null);
  const [saving, setSaving] = useState(false);
  const rows = preview?.rows ?? [];

  return (
    <Modal
      destroyOnClose
      open={open}
      title="导入库位清单"
      okText="导入这份库位清单"
      cancelText="先不导入"
      okButtonProps={{ disabled: rows.length === 0, loading: saving }}
      onCancel={onCancel}
      afterClose={() => setPreview(null)}
      onOk={() => {
        if (!preview || preview.rows.length === 0) return;
        setSaving(true);
        void commitImport({ kind: "location", rows: preview.rows })
          .then((result) => {
            message.success(`将新建 ${result.written} 个库位，跳过 ${result.skipped} 个已经有的。`);
            onDone();
          })
          .catch((error: unknown) => message.error(readError(error)))
          .finally(() => setSaving(false));
      }}
    >
      <Upload.Dragger
        accept=".csv,.xlsx"
        maxCount={1}
        beforeUpload={(file) => {
          void previewImport("location", file)
            .then((result) => {
              const locationRows = result.rows.filter(isLocationImportRow);
              setPreview({
                total: result.total,
                rows: locationRows,
                issues: result.issues,
                created: result.created,
                skipped: result.skipped,
                rejected: result.rejected,
              });
            })
            .catch((error: unknown) => {
              setPreview(null);
              message.error(readError(error));
            });
          return false;
        }}
      >
        <p>{LOCATION_IMPORT_DROP_HINT}</p>
      </Upload.Dragger>
      <Button
        style={{ marginTop: 12 }}
        onClick={() => {
          const blob = new Blob([LOCATION_IMPORT_TEMPLATE], { type: "text/csv;charset=utf-8" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "库位清单模板.csv";
          link.click();
          URL.revokeObjectURL(url);
        }}
      >
        下载一份空白模板
      </Button>
      {preview ? (
        <Alert
          style={{ marginTop: 12 }}
          type="info"
          showIcon
          message={`将新建 ${preview.created ?? 0} 个库位，跳过 ${preview.skipped ?? 0} 个已经有的，拒绝 ${preview.rejected ?? preview.issues.length} 行。`}
        />
      ) : null}
      {preview?.issues.map((issue) => (
        <Typography.Paragraph key={`${issue.row}-${issue.message}`} type="danger" style={{ marginBottom: 0 }}>
          {issue.message}
        </Typography.Paragraph>
      ))}
    </Modal>
  );
}

